import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import AcademicBoardCard from '../components/AcademicBoardCard';
import AdminEditModal from '../components/AdminEditModal';
import ConfirmModal from '../components/ConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditorialBoardPage = () => {
  const { user } = useAuth();
  const { editBoardMember, deleteBoardMember } = useData();

  const [boardMembers, setBoardMembers] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isAdmin = user && (user.role === 'admin' || user.role === 'superadmin');

  useEffect(() => {
    const fetchBoardMembers = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/editorial-board`);
        const data = await res.json();

        // ✅ FINAL FIX: USE BACKEND CATEGORY (DO NOT OVERRIDE)
        const normalized = data.map(u => ({
          ...u,
          category: u.category || 'reviewer', // fallback only if missing
        }));

        setBoardMembers(normalized);
      } catch (err) {
        console.error("Failed to fetch editorial board:", err);
      }
    };

    if (BACKEND_URL) fetchBoardMembers();
  }, []);

  // ✅ CLEAN SEPARATION
  const founders = boardMembers.filter(m => m.category === 'founder');
  const editors = boardMembers.filter(m => m.category === 'editor');
  const reviewers = boardMembers.filter(m => m.category === 'reviewer');

  const handleSaveEdit = (updatedData) => {
    editBoardMember(editItem.id, updatedData);
    setEditItem(null);
  };

  const handleConfirmDelete = () => {
    deleteBoardMember(deleteTarget.id);
    setDeleteTarget(null);
  };

  const renderGrid = (members) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {members.map(member => (
        <AcademicBoardCard
          key={member.id}
          member={member}
          isAdmin={isAdmin}
          onEdit={setEditItem}
          onDelete={setDeleteTarget}
        />
      ))}
    </div>
  );

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="bg-[#F5F7FA] border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 py-14">
          <p className="section-overline mb-3">Leadership & Governance</p>
          <h1 className="font-serif text-5xl font-semibold text-[#1F1F1F] mb-4">
            Editorial Board
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">

        {/* 🔴 FOUNDERS */}
        {founders.length > 0 && (
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <h2 className="font-serif text-3xl font-semibold text-pink-600">
                Founders
              </h2>
              <div className="flex-1 h-px bg-pink-200" />
              <span className="text-sm text-pink-500">
                {founders.length} members
              </span>
            </div>

            <div className="bg-pink-50 border border-pink-100 p-5 rounded-lg">
              {renderGrid(founders)}
            </div>
          </div>
        )}

        {/* 🔵 EDITORS */}
        {editors.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-5">
              <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F]">
                Editors
              </h2>
              <div className="flex-1 h-px bg-[#E5E7EB]" />
              <span className="text-sm text-[#9CA3AF]">
                {editors.length} members
              </span>
            </div>

            {renderGrid(editors)}
          </div>
        )}

        {/* 🟣 REVIEWERS */}
        {reviewers.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-5">
              <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F]">
                Associate Editors & Reviewers
              </h2>
              <div className="flex-1 h-px bg-[#E5E7EB]" />
              <span className="text-sm text-[#9CA3AF]">
                {reviewers.length} members
              </span>
            </div>

            {renderGrid(reviewers)}
          </div>
        )}

      </div>

      <AdminEditModal
        isOpen={!!editItem}
        type="board"
        item={editItem}
        onSave={handleSaveEdit}
        onClose={() => setEditItem(null)}
      />

      <ConfirmModal
        isOpen={!!deleteTarget}
        message={`Are you sure you want to remove "${deleteTarget?.name}" from the editorial board?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default EditorialBoardPage;
